/** Rate/fee row fields involved when remittance basis is locked to gross reported. */
export interface RateFeeLineItemRemittanceBasisSync {
  isRemittanceBasisAmountEnabled?: boolean;
  remittanceBasisReported?: number;
  remittanceBasisCalculated?: number;
  isRemittanceBasisOverridden?: boolean;
}

export type RateFeeAmountErrorType = 'netExceedsGross' | 'remittanceBasisExceedsGross' | 'netExceedsRemittanceBasis';

export enum RateFeeReportedField {
  Gross = 'grossReported',
  Net = 'netReported',
  RemittanceBasis = 'remittanceBasisReported',
}

export interface RateFeeAmountValidationFields extends RateFeeLineItemRemittanceBasisSync {
  grossReported: number;
  netReported: number;
  remittanceBasisReported: number;
  remittanceBasisCalculated: number;
  isError?: boolean;
  isRemittanceBasisTooHigh?: boolean;
  isNetTooHigh?: boolean;
  amountErrorType?: RateFeeAmountErrorType;
  additionalFees?: RateFeeAmountValidationFields[];
}

export const RATE_FEE_AMOUNT_ERROR_MESSAGES: Record<RateFeeAmountErrorType, string> = {
  netExceedsGross: 'Net Values cannot exceed gross, please update before calculating.',
  remittanceBasisExceedsGross: 'Remittance Basis Values cannot exceed Gross, please update before calculating.',
  netExceedsRemittanceBasis: 'Net Values cannot exceed Remittance Basis, please update before calculating.',
};

/**
 * When remittance basis is not user-editable, set basis amounts to gross reported and keep
 * {@link RateFeeLineItemRemittanceBasisSync.isRemittanceBasisOverridden} in sync with the same
 * rule as manual remittance edits (true only when reported differs from calculated).
 * Override styling on the disabled field is still suppressed in ReportedFeesTable when locked by product.
 */
export const syncGrossAndRemittanceBasis = (
  rateFeeLineItem: RateFeeLineItemRemittanceBasisSync,
  grossReported: number,
) => {
  if (rateFeeLineItem.isRemittanceBasisAmountEnabled !== false) {
    return;
  }
  rateFeeLineItem.remittanceBasisReported = grossReported;
  rateFeeLineItem.remittanceBasisCalculated = grossReported;
  rateFeeLineItem.isRemittanceBasisOverridden = !(
    rateFeeLineItem.remittanceBasisCalculated === rateFeeLineItem.remittanceBasisReported
  );
};

export const applyRateFeeAmountValidation = (item: RateFeeAmountValidationFields, isRemittanceBasisRate: boolean) => {
  item.isRemittanceBasisTooHigh = false;
  item.isNetTooHigh = false;
  item.amountErrorType = undefined;

  if (isRemittanceBasisRate) {
    const remittanceBasis = item.remittanceBasisReported ?? item.remittanceBasisCalculated;
    if (remittanceBasis > item.grossReported) {
      item.isRemittanceBasisTooHigh = true;
      item.amountErrorType = 'remittanceBasisExceedsGross';
    }
    if (item.netReported > remittanceBasis) {
      item.isNetTooHigh = true;
      if (!item.amountErrorType) {
        item.amountErrorType = 'netExceedsRemittanceBasis';
      }
    } else if (item.netReported > item.grossReported) {
      item.isNetTooHigh = true;
      if (!item.amountErrorType) {
        item.amountErrorType = 'netExceedsGross';
      }
    }
  } else if (item.netReported > item.grossReported) {
    item.isNetTooHigh = true;
    item.amountErrorType = 'netExceedsGross';
  }

  item.isError = Boolean(item.isRemittanceBasisTooHigh || item.isNetTooHigh);
};

export const validateAllRateFeeLineItems = <T extends RateFeeAmountValidationFields>(
  lineItems: T[],
  isRemittanceBasisRate: boolean,
): T[] => {
  return lineItems.map((lineItem) => {
    applyRateFeeAmountValidation(lineItem, isRemittanceBasisRate);
    if (lineItem.additionalFees?.length) {
      lineItem.additionalFees = validateAllRateFeeLineItems(lineItem.additionalFees, isRemittanceBasisRate);
    }
    return lineItem;
  });
};

export const withRateFeeLineItemValidation = <
  T extends { rateFeeLineItems?: RateFeeAmountValidationFields[]; isRemittanceBasisRate?: boolean },
>(
  rateAndFeeData: T,
): T => {
  if (rateAndFeeData?.rateFeeLineItems?.length) {
    validateAllRateFeeLineItems(rateAndFeeData.rateFeeLineItems, rateAndFeeData.isRemittanceBasisRate === true);
  }
  return rateAndFeeData;
};

export const findFirstRateFeeAmountError = (
  lineItems: RateFeeAmountValidationFields[],
  isRemittanceBasisRate: boolean,
): RateFeeAmountErrorType | null => {
  for (const lineItem of lineItems) {
    if (lineItem.isRemittanceBasisTooHigh) {
      return 'remittanceBasisExceedsGross';
    }
    if (lineItem.isNetTooHigh) {
      const remittanceBasis = lineItem.remittanceBasisReported ?? lineItem.remittanceBasisCalculated;
      if (isRemittanceBasisRate && lineItem.netReported > remittanceBasis) {
        return 'netExceedsRemittanceBasis';
      }
      return 'netExceedsGross';
    }
    if (lineItem.additionalFees?.length) {
      const nestedError = findFirstRateFeeAmountError(lineItem.additionalFees, isRemittanceBasisRate);
      if (nestedError) {
        return nestedError;
      }
    }
  }
  return null;
};
